import { atom, Getter, useAtomValue } from 'jotai';

import { useDeepMemo } from '@crossnokaye/ux-common';

import {
  Namespace,
  Ref,
  Schedule,
  Sequence,
  Setting,
  SettingValue,
} from '@repo/sdk/models/components';

import { readingsFamily } from '@/hooks/query-hooks/streams/gaia-api/use-readings-stream';
import { settingsFamily } from '@/hooks/query-hooks/streams/gaia-api/use-settings-stream';
import { LatestValue } from '@/types/reading-types';

/**
 * Low-level atom-getter function that reads a single ref value from Jotai state.
 * Use this inside atom() derivations where hooks cannot be called.
 * For React components, prefer useGetRefValue instead.
 */
export const readRefAtomValue = (
  get: Getter,
  ref: Ref | undefined
): string | number | boolean | Schedule | Sequence | undefined => {
  if (!ref) return undefined;
  if (ref.namespace === Namespace.Setting) {
    const setting = get(settingsFamily({ settingId: ref.id } as Setting));
    return setting?.value?.[setting?.kind as keyof SettingValue];
  }
  const reading = get(readingsFamily({ pointId: ref.id } as LatestValue));
  return reading?.[reading?.kind as keyof LatestValue] as string | number | boolean | undefined;
};

/**
 * Reads a single ref value reactively from Jotai state.
 * Handles both reading and setting refs based on namespace.
 *
 * @param ref - The ref to get the value of
 * @returns The current value of the ref
 */
export const useGetRefValue = (ref: Ref | undefined) => {
  const derivedAtom = useDeepMemo(() => {
    return atom((get) => readRefAtomValue(get, ref));
  }, [ref]);

  return useAtomValue(derivedAtom);
};
